import { useState } from "react";
import { CalendarDays, ExternalLink, MapPin, Trophy } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";

import { Button } from "@/components/ui/button";
import { formatDate, formatDateRange } from "@/utils/format";
import { SponsoredCarousel } from "@/components/SponsoredCarousel";
import { CalendarLinks } from "@/components/CalendarLinks";
import { HeroChronicleStrip } from "@/components/HeroChronicleStrip";

const HERO_IMAGE = null;

function pickHeroImage(images) {
  if (!images.length) return HERO_IMAGE;
  const previous = Number(window.localStorage.getItem("todotiro-hero-index") ?? -1);
  const index = (previous + 1) % images.length;
  window.localStorage.setItem("todotiro-hero-index", String(index));
  return images[index].url;
}
const headline = ["Tiro al plato", "nacional", "en clave editorial"];

export function HeroSection({ calendar = [], stats, sponsored = [], heroImages = [], chronicles = [] }) {
  const [heroImage] = useState(() => pickHeroImage(heroImages));
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 700], [0, 130]);
  const nextEvent = calendar.find((event) => new Date(`${event.date}T12:00:00`) >= new Date(new Date().setHours(0, 0, 0, 0))) || calendar[0];
  const eventDate = nextEvent ? new Date(`${nextEvent.date}T12:00:00`) : null;
  const daysLeft = eventDate ? Math.max(0, Math.ceil((eventDate - new Date()) / 86400000)) : 0;

  return (
    <section id="portada" className="relative overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imageY }}>
        {heroImage ? <img src={heroImage} alt="Cancha de tiro al plato" data-testid="hero-photo" className="hero-photo h-[112%] w-full object-cover object-center" /> : null}
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(13,15,18,0.86)_0%,rgba(13,15,18,0.42)_43%,rgba(13,15,18,0.08)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_35%,rgba(255,107,0,0.20),transparent_28rem)]" />
      <div className="noise-overlay" />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-6 px-5 pb-6 pt-24 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
        <div>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="eyebrow mb-5" data-testid="hero-eyebrow">
            Temporada 2026 · foso universal · trap 5 · foso olímpico
          </motion.p>
          <h1 className="font-display text-4xl uppercase leading-[0.82] tracking-tight text-slate-50 drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)] sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {headline.map((line, index) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={index === 1 ? "block text-clay" : "block"}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.18 + index * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.7 }} className="mt-4 max-w-xl text-sm leading-7 text-slate-100 drop-shadow-md sm:text-base" data-testid="hero-description">
            Una portada viva para seguir la semana de competición, abrir carteles, consultar clasificaciones y leer la crónica de cada podio.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }} className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="h-11 rounded-none bg-clay px-6 font-mono text-xs uppercase tracking-[0.25em] text-charcoal hover:bg-brass" data-testid="hero-events-cta">
              <a href="#semana">Ver tiradas de la semana</a>
            </Button>
          </motion.div>
        </div>

        {sponsored.length ? (
          <motion.div initial={{ opacity: 0, x: 44 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.55, duration: 0.9 }} className="w-full max-w-2xl justify-self-end">
            <SponsoredCarousel shoots={sponsored} calendar={calendar} />
          </motion.div>
        ) : (
        <motion.aside initial={{ opacity: 0, x: 44 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.55, duration: 0.9 }} className="floating-panel w-full max-w-xl justify-self-end p-6 sm:p-7" data-testid="next-event-card">
          <div className="mb-6 flex items-start justify-between gap-6">
            <div>
              <p className="eyebrow">Próxima tirada</p>
              <h2 className="mt-3 max-w-xs font-display text-4xl uppercase leading-[0.86] text-slate-50">{nextEvent?.title || "Calendario en preparación"}</h2>
            </div>
            <div className="border border-clay/40 bg-charcoal/45 px-4 py-3 text-center backdrop-blur">
              <span className="block font-display text-5xl leading-none text-clay" data-testid="next-event-countdown">{daysLeft}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">días</span>
            </div>
          </div>
          <div className="space-y-3 text-sm text-slate-200">
            <p className="flex items-center gap-3"><CalendarDays className="h-4 w-4 text-clay" />{nextEvent ? formatDateRange(nextEvent.date, nextEvent.end_date) : "Por confirmar"}</p>
            <p className="flex items-center gap-3"><MapPin className="h-4 w-4 text-clay" />{nextEvent ? `${nextEvent.club ? `${nextEvent.club} · ` : ""}${nextEvent.location}` : "Campo de tiro"}</p>
            <p className="flex items-center gap-3"><Trophy className="h-4 w-4 text-clay" />{nextEvent?.modality || "Tiro al plato"}</p>
            <div className="flex flex-wrap gap-2">
              {nextEvent?.poster_url ? (
                <a href={nextEvent.poster_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-clay/40 bg-clay/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-clay transition-colors hover:bg-clay hover:text-charcoal" data-testid="next-event-poster-link">Cartel <ExternalLink className="h-3 w-3" /></a>
              ) : null}
              <CalendarLinks item={nextEvent} prefix="next-event-results-link" size="lg" />
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 border-t border-white/15 pt-5 text-center">
            <div data-testid="hero-stat-events"><span className="stat-number">{stats?.tiradas ?? calendar.length}</span><span className="stat-label">Tiradas</span></div>
            <div data-testid="hero-stat-chronicles"><span className="stat-number">{stats?.cronicas ?? 0}</span><span className="stat-label">Crónicas</span></div>
            <div data-testid="hero-stat-archive"><span className="stat-number">{stats?.hemeroteca ?? 0}</span><span className="stat-label">Hemeroteca</span></div>
          </div>
        </motion.aside>
        )}
        <HeroChronicleStrip chronicles={chronicles} />
      </div>


    </section>
  );
}
